import './ManageOrdersTable.scss';

import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  CollectionPreferences,
  Container,
  DateRangePicker,
  DateRangePickerProps,
  Grid,
  Header,
  Multiselect,
  MultiselectProps,
  Pagination,
  PaginationProps,
  PropertyFilter,
  PropertyFilterProps,
  SpaceBetween,
  Spinner,
  Table,
  TableProps
} from '@amzn/awsui-components-react';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { Order } from '@amzn/ito-client';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { ColumnSetting } from 'common/utils/config-models';
import { VENDORS } from 'configuration/config';
import { usePermissions } from 'common/permissions/PermissionsContext';
import { usePreferences } from 'hooks/usePreferences';
import _ from 'lodash';
import { FunctionComponent, useEffect, useState } from 'react';
import { FilesService } from 'services/files-service';

import EmptyTableBox from '../../common/EmptyTableBox/EmptyTableBox';
import { getPropertyFilterString, getString } from '../../common/ui-string-labels/ui-strings-utils';
import {
  buildColumnDefinitions,
  buildFilteringProperties,
  buildSelectOptionsFromStrings,
  buildVisibleContentOptions,
  defaultPagingOptions,
  SortingState} from '../../common/utils';
import { convertTokensToPropertyFilter } from '../../common/utils';
import { buildDateRangeFilter, DATE_RANGE_RELATIVE_OPTIONS, formatRelativeRange } from '../../common/utils/date-range-filter-utils';
import { useGetOrders } from '../../hooks/orders';
import { CurrentPage } from '../../interfaces';
import { ManageOrdersTableHeader } from './ManageOrdersTableHeader';
import ordersTableConfig from './orders.table-config.json';
import { useDetailsSidePanelResult, useManagerOrdersTableResult } from './useManageOrdersTable';

const PREFERENCES_KEY = 'ManageOrdersPreferences';
const columns: ColumnSetting[] = ordersTableConfig.columns;
const FILTERING_PROPERTIES = buildFilteringProperties(columns);
const VISIBLE_CONTENT_COLUMN_OPTIONS = buildVisibleContentOptions(columns);

const STATUS_OPTIONS = [
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Denied', value: 'DENIED' },
  { label: 'Pending Approval', value: 'PENDING_APPROVAL' },
  { label: 'Processing', value: 'PROCESSING' },
  { label: 'Pending Reseller', value: 'PENDING_RESELLER' }
];

const formatUnit = (unit: string, value: number) => (value === 1 ? unit : `${unit}s`);

const uploadedOrdersVisibleColumns = [
  'display',
  'requested.on',
  'requested.by',
  'assignedTo',
  'orderReference',
  'status',
  'uploadError'
];

export interface ManageOrdersTableProps {
  useFlashBarItemsActions?: useFlashBarItemsActions;
  useDetailsSidePanel?: useDetailsSidePanelResult;
  useManageOrdersTableHook?: useManagerOrdersTableResult;
  fileId?: string;
  fileName?: string;
}

export const ManageOrdersTable: FunctionComponent<ManageOrdersTableProps> = (props: ManageOrdersTableProps) => {
  // Actions
  const { useFlashBarItemsActions, useDetailsSidePanel, useManageOrdersTableHook } = props;
  const { attributes, actions } = useManageOrdersTableHook!;
  const [loading, setLoading] = useState(false);
  const { hasGspTracker } = usePermissions();

  const [currentSorting, setCurrentSorting] = useState<SortingState | undefined>(undefined);

  useEffect(() => {
    showHideOrderDetailsSidePanel(attributes.selectedItems, useDetailsSidePanel);
  }, [attributes.selectedItems]);

  const url = new URL(window.location.href);

  // Preferences
  const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
    preferencesKey: PREFERENCES_KEY,
    refreshData: () => refreshData(true),
    columnsSettings: columns,
    defaultPaging: defaultPagingOptions[0].value
  });

  // Checks for specific order id in the query params
  let orderIdToShow: string | null = null;
  if (url.searchParams.has('orderId')) {
    orderIdToShow = url.searchParams.get('orderId');
  }

  // Filters and pagination
  const [currentFilteringQuery, setFilteringQuery] = useState<PropertyFilterProps.Query>({
    tokens: orderIdToShow
      ? [
          {
            operator: '=',
            value: orderIdToShow,
            propertyKey: 'orderId'
          }
        ]
      : [],
    operation: 'and'
  });
  const [currentPageIndex, setCurrentPageIndex] = useState<CurrentPage>({ index: 1, isPageReset: true });
  const [selectedStatus, setSelectedStatus] = useState<SelectProps.Option[]>([]);

  const VENDOR_OPTIONS: SelectProps.Option[] = [
    { label: getString('manageOrders.filters.vendorPlaceholder'), value: '' },
    ...VENDORS.map(v => ({ label: v.label, value: v.id })),
  ];
  const [selectedVendor, setSelectedVendor] = useState(VENDOR_OPTIONS[0]);

  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);

  const ASSIGNED_TO_OPTIONS: SelectProps.Option[] = [
    { label: getString('manageOrders.filters.assignedToPlaceholder'), value: '' },
    ...buildSelectOptionsFromStrings(attributes.procurementMembersSystem)
  ];
  const [selectedAssignee, setSelectedAssignee] = useState(ASSIGNED_TO_OPTIONS[0]);

  // Errors
  const [error, setError] = useState<Error | undefined>(undefined);

  // To get orders from ITO
  const [ordersData, isLoading, doGetOrders] = useGetOrders(
    { pageSize: currentPreferences.pageSize, pageIndex: 1 },
    undefined
  );

  useEffect(() => {
    refreshData(true);
  }, [selectedAssignee, selectedStatus, selectedVendor, dateRange, currentFilteringQuery, currentSorting?.sortBy, currentSorting?.sortOrder]); 

  useEffect(() => {
    if (!currentPageIndex.isPageReset) {
      refreshData(false);
    }
  }, [currentPageIndex.index, currentPageIndex.isPageReset]);

  // Filtering Events
  /* istanbul ignore next */
  const onPropertyFilterChange: NonCancelableEventHandler<PropertyFilterProps.Query> = e => {
    setFilteringQuery(e.detail);
  };

  /* istanbul ignore next */
  const onStatusChange: NonCancelableEventHandler<MultiselectProps.MultiselectChangeDetail> = ({ detail }) => {
    setSelectedStatus(detail.selectedOptions as SelectProps.Option[]);
  };

  const onAssignedChange: NonCancelableEventHandler<SelectProps.ChangeDetail> = e => {
    setSelectedAssignee({
      label: e.detail.selectedOption.label!,
      value: e.detail.selectedOption.value!
    });
  };

  // Sorting
  /* istanbul ignore next */
  const onSortingChange: NonCancelableEventHandler<TableProps.SortingState<Order>> = async e => {
    const sortBy = e.detail.sortingColumn.sortingField;
    const sortOrder = e.detail.isDescending ? 'sort.desc' : 'sort.asc';
    setCurrentSorting({ sortBy, sortOrder });
    collectionProps.onSortingChange!(e);
  };

  const { collectionProps } = useCollection(ordersData?.orders ?? [], {
    sorting: {}
  });

  // Pagination
  /* istanbul ignore next */
  const onPageClick: NonCancelableEventHandler<PaginationProps.ChangeDetail> = e => {
    setCurrentPageIndex({ index: e.detail.currentPageIndex, isPageReset: false });
  };

  /* istanbul ignore next */
  const refreshData = (clearPreviousData: boolean) => {
    let pageIndexForQuery = currentPageIndex.index;
    if (clearPreviousData) {
      pageIndexForQuery = 1;
      setCurrentPageIndex({ index: 1, isPageReset: true });
    }

    const filters: { [key: string]: string[] } = convertTokensToPropertyFilter(currentFilteringQuery.tokens);

    if (selectedStatus.length > 0) {
      filters.status = selectedStatus.map(s => s.value!);
    }
    if (selectedVendor.value) {
      filters['items.details.vendor'] = [selectedVendor.value];
    }
    const dateFilter = buildDateRangeFilter(dateRange);
    if (dateFilter) {
      filters['requested.on'] = dateFilter
    }
    if (selectedAssignee.value) {
      filters.assignedTo = [selectedAssignee.value];
    }
    if (currentSorting && currentSorting.sortOrder && currentSorting.sortBy) {
      filters[currentSorting.sortOrder] = [currentSorting.sortBy];
    }

    // Add filter by file id
    if (props.fileId) {
      filters['uploadInformation.fileId'] = [props.fileId];
    }
    
    actions.refreshAttributes();
    doGetOrders({
      filters,
      pageSize: currentPreferences.pageSize!,
      pageIndex: pageIndexForQuery
    });
  };

  const onDownloadOrders = async () => {
    setLoading(true);
    await FilesService.instance.downloadOrders();
    refreshData(true);
    setLoading(false);
  };

  // Export can poll for up to ~18 minutes (see FilesService.exportFilteredOrders), so it must
  // not reuse the shared `loading` state above — that flag is meant for short refresh/download
  // operations, and a future consumer that gates UI on `loading` would otherwise get stuck for
  // the full export duration. Scope the export's own in-flight flag separately.
  const [isExporting, setIsExporting] = useState(false);

  const onDownloadFilteredData = async () => {
    const filters: { [key: string]: string[] } = convertTokensToPropertyFilter(currentFilteringQuery.tokens);

    if (selectedStatus.length > 0) {
      filters.status = selectedStatus.map(s => s.value!);
    }
    if (selectedVendor.value) {
      filters['items.details.vendor'] = [selectedVendor.value];
    }
    const dateFilter = buildDateRangeFilter(dateRange);
    if (dateFilter) {
      filters['requested.on'] = dateFilter;
    }
    if (selectedAssignee.value) {
      filters.assignedTo = [selectedAssignee.value];
    }

    if (Object.keys(filters).length === 0) {
      useFlashBarItemsActions!.addErrorFlashBarItem('Please apply at least one filter before exporting.');
      return;
    }

    try {
      setIsExporting(true);
      useFlashBarItemsActions!.clearFlashBarItems?.();
      useFlashBarItemsActions!.addLoadingFlashBarItem?.('Exporting orders — this may take a moment...');
      await FilesService.instance.exportFilteredOrders(filters);
      useFlashBarItemsActions!.clearFlashBarItems?.();
      useFlashBarItemsActions!.addSuccessFlashBarItem('Export completed — file downloaded.');
    } catch (error: any) {
      useFlashBarItemsActions!.clearFlashBarItems?.();
      useFlashBarItemsActions!.addErrorFlashBarItem(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Ready to display
  let pagesCount = 0;
  let totalResults = 0;
  if (ordersData && ordersData.totalResults && ordersData.totalResults > 0) {
    totalResults = ordersData.totalResults;
    pagesCount = Math.ceil(ordersData.totalResults / currentPreferences.pageSize!);
  }

  const uploadErrorColumnIndex = columns.findIndex(c => c.id === 'uploadError');
  if (props.fileId && uploadErrorColumnIndex >= 0) {
    currentPreferences.visibleContent = uploadedOrdersVisibleColumns;
    columns[uploadErrorColumnIndex].visible = 'yes';
  } else {
    if (uploadErrorColumnIndex >= 0) {
      columns[uploadErrorColumnIndex].visible = 'never';
    }
  }

  const currentColumnDefinitions = buildColumnDefinitions(columns, currentPreferences.visibleContent);

  if (loading) {
    return (
      <Container>
        <Header variant="h2" data-testid="download-loading">
          {getString('manageOrders.table.downloadloading')}
        </Header>
        <Spinner size="large" />
      </Container>
    );
  }

  return (
    <Box data-testid="manage-orders-box">
      <Table
        {...collectionProps}
        data-testid="manage-orders-table"
        loading={isLoading}
        onSelectionChange={actions.onSelectionChangeHandler}
        selectedItems={attributes.selectedItems}
        header={
          <ManageOrdersTableHeader
            totalResults={totalResults}
            selectedItems={attributes.selectedItems!}
            refreshData={refreshData}
            useFlashBarItemsActions={useFlashBarItemsActions!}
            procurementMembersSelectOptions={buildSelectOptionsFromStrings(attributes.procurementMembers)}
            onDownloadOrders={onDownloadOrders}
            onDownloadFilteredData={hasGspTracker ? onDownloadFilteredData : undefined}
            isExportingFilteredData={isExporting}
            fileId={props.fileId}
            fileName={props.fileName}
          />
        }
        filter={
          <SpaceBetween size="s">
            <Grid gridDefinition={[{ colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]}>
              <PropertyFilter
                expandToViewport
                query={currentFilteringQuery}
                onChange={onPropertyFilterChange}
                filteringProperties={FILTERING_PROPERTIES}
                i18nStrings={getPropertyFilterString()}
                hideOperations={true}
              />
              <Multiselect
                data-testid="select-status"
                selectedOptions={selectedStatus}
                onChange={onStatusChange}
                options={STATUS_OPTIONS}
                placeholder={getString('manageOrders.filters.statusPlaceholder')}
                selectedAriaLabel="Selected"
              />
              {hasGspTracker && <Select
                data-testid="select-vendor"
                selectedOption={selectedVendor}
                onChange={({ detail }) => setSelectedVendor(detail.selectedOption)}
                options={VENDOR_OPTIONS}
                selectedAriaLabel="Selected"
              />}
              <Select
                data-testid="select-assigned"
                selectedOption={selectedAssignee}
                onChange={onAssignedChange}
                options={ASSIGNED_TO_OPTIONS}
                selectedAriaLabel="Selected"
              />
              <DateRangePicker
                value={dateRange}
                onChange={({ detail }) => setDateRange(detail.value)}
                placeholder={getString('manageOrders.filters.dateRangePlaceholder')}
                relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
                isValidRange={() => ({ valid: true })}
                i18nStrings={{
                  todayAriaLabel: getString('manageOrders.dateRangePicker.todayAriaLabel'),
                  nextMonthAriaLabel: getString('manageOrders.dateRangePicker.nextMonthAriaLabel'),
                  previousMonthAriaLabel: getString('manageOrders.dateRangePicker.previousMonthAriaLabel'),
                  customRelativeRangeDurationLabel: getString('manageOrders.dateRangePicker.customRelativeRangeDurationLabel'),
                  customRelativeRangeDurationPlaceholder: getString('manageOrders.dateRangePicker.customRelativeRangeDurationPlaceholder'),
                  customRelativeRangeOptionLabel: getString('manageOrders.dateRangePicker.customRelativeRangeOptionLabel'),
                  customRelativeRangeOptionDescription: getString('manageOrders.dateRangePicker.customRelativeRangeOptionDescription'),
                  customRelativeRangeUnitLabel: getString('manageOrders.dateRangePicker.customRelativeRangeUnitLabel'),
                  formatRelativeRange,
                  formatUnit,
                  relativeModeTitle: getString('manageOrders.dateRangePicker.relativeModeTitle'),
                  absoluteModeTitle: getString('manageOrders.dateRangePicker.absoluteModeTitle'),
                  relativeRangeSelectionHeading: getString('manageOrders.dateRangePicker.relativeRangeSelectionHeading'),
                  startDateLabel: getString('manageOrders.dateRangePicker.startDateLabel'),
                  endDateLabel: getString('manageOrders.dateRangePicker.endDateLabel'),
                  startTimeLabel: getString('manageOrders.dateRangePicker.startTimeLabel'),
                  endTimeLabel: getString('manageOrders.dateRangePicker.endTimeLabel'),
                  clearButtonLabel: getString('manageOrders.dateRangePicker.clearButtonLabel'),
                  cancelButtonLabel: getString('manageOrders.dateRangePicker.cancelButtonLabel'),
                  applyButtonLabel: getString('manageOrders.dateRangePicker.applyButtonLabel'),
                  dateTimeConstraintText: '',
                }}
                dateOnly
              />
            </Grid>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex.index}
            pagesCount={pagesCount}
            onChange={onPageClick}
            ariaLabels={{
              nextPageLabel: getString('common.pagination.nextPage'),
              previousPageLabel: getString('common.pagination.nextPage'),
              pageLabel: (pageNumber: number) => getString('common.pagination.currentPage', { pageNumber, pagesCount })
            }}
          />
        }
        onSortingChange={onSortingChange}
        empty={<EmptyTableBox />}
        stripedRows={currentPreferences.stripedRows}
        preferences={
          props.fileId ? undefined : (
            <CollectionPreferences
              title={getString('common.preferences.title')}
              confirmLabel={getString('common.preferences.confirmLabel')}
              cancelLabel={getString('common.preferences.cancelLabel')}
              pageSizePreference={{
                title: getString('common.preferences.pageSize.title'),
                options: defaultPagingOptions
              }}
              stripedRowsPreference={{
                label: getString('common.preferences.stripedRows.label'),
                description: getString('common.preferences.stripedRows.description')
              }}
              visibleContentPreference={{
                title: getString('common.preferences.visibleContentPreference.title'),
                options: [
                  {
                    label: getString('common.preferences.visibleContentPreference.columns'),
                    options: VISIBLE_CONTENT_COLUMN_OPTIONS
                  }
                ]
              }}
              preferences={currentPreferences}
              onConfirm={onPreferencesConfirmHandler}
            />
          )
        }
        columnDefinitions={currentColumnDefinitions}
        loadingText={getString('common.loading')}
        items={ordersData?.orders!}
        resizableColumns={true}
        selectionType="multi"
        variant="full-page"
        wrapLines
      />
    </Box>
  );
};

function showHideOrderDetailsSidePanel(
  selectedItems: Order[],
  useDetailsSidePanel: useDetailsSidePanelResult | undefined
) {
  if (selectedItems && useDetailsSidePanel) {
    if (selectedItems.length === 1) {
      useDetailsSidePanel.actions.showPanel(selectedItems[0]);
    } else if (useDetailsSidePanel.isOpened) {
      useDetailsSidePanel.actions.hidePanel();
    }
  }
}
